<script setup lang="ts">
import { computed } from 'vue';
import MonacoEditor from '@guolao/vue-monaco-editor';
import { useLayout } from 'src/composables/useLayout';
import { lightTheme, darkTheme } from 'src/config/monaco-themes';
import type * as monaco from 'monaco-editor';

const props = defineProps<{
  modelValue: string;
  language?: string;
  height?: string;
  options?: Record<string, any>;
}>();

const emit = defineEmits<{
  'update:modelValue': [value: string];
}>();

const { layoutConfig } = useLayout();

const editorTheme = computed(() => layoutConfig.value.darkTheme ? 'primevue-dark' : 'primevue-light');

const editorOptions = computed(() => ({
  theme: editorTheme.value,
  automaticLayout: true,
  minimap: { enabled: false },
  lineNumbers: 'on',
  folding: true,
  scrollBeyondLastLine: false,
  wordWrap: 'on',
  formatOnPaste: true,
  formatOnType: true,
  fontSize: 14,
  tabSize: 2,
  ...props.options,
}));

function handleChange(value: string) {
  emit('update:modelValue', value);
}

function handleBeforeMount(monacoInstance: typeof monaco) {
  // Define both themes
  monacoInstance.editor.defineTheme('primevue-light', lightTheme);
  monacoInstance.editor.defineTheme('primevue-dark', darkTheme);
  // Set the current theme
  monacoInstance.editor.setTheme(editorTheme.value);
}
</script>

<template>
  <div class="border-1 surface-border border-round">
    <MonacoEditor
      :value="modelValue"
      :language="language"
      v-bind="editorOptions"
      :height="height ?? '200px'"
      class="w-full"
      @change="handleChange"
      @beforeMount="handleBeforeMount"
    />
  </div>
</template>

<style>
.monaco-editor {
  padding: 0.5rem 0;
}

.monaco-editor .overflow-guard {
  border-radius: var(--p-inputtext-border-radius);
}
</style>
